// AuthBootstrap — hidrata a sessão no mount do app.
//
// Problema resolvido: ao dar refresh em /inbox, o slice auth zera (default state),
// `RequireAuth` lê isAuthed=false e redireciona pra /login. O token, porém,
// ainda vive em sessionStorage. Aqui chamamos /v1/me com o token persistido
// (ou tentamos refresh via cookie httpOnly) antes de liberar o router.
//
// SRP: orquestra a hidratação. Não decide rota — só garante que Redux reflete a
// realidade do token persistido.
//
// ## Erro de rede NÃO é sessão inválida
//
// Este componente já apagou sessões que estavam perfeitamente válidas: o `catch` era
// único e chamava `clearToken()`, que também removia a flag `chatia:has-session`.
// Bastava a API estar reiniciando (502), o KeyDB congelado (504) ou o usuário abrir o
// app offline para a sessão ser destruída — e, sem a flag, o carregamento seguinte
// nem tentava retomar pelo cookie. Um soluço de infraestrutura virava "digite sua
// senha de novo". Agora só uma recusa definitiva do servidor encerra a sessão.
import { useEffect, useState, type ReactNode } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { Splash } from '@/components/ui/Splash';
import { getToken, clearSession, hasPriorSession } from '@/lib/auth/token-storage';
import { refreshAccessToken } from '@/lib/auth/refresh';
import { startTokenRefreshScheduler } from '@/lib/auth/refresh-scheduler';
import { authApi } from './api';
import { sessionStarted } from './slice';

interface AuthBootstrapProps {
  children: ReactNode;
}

/**
 * `me` falhou: foi recusa de credencial ou o backend estava fora?
 *
 * RTK Query devolve `FETCH_ERROR`/`TIMEOUT_ERROR` para rede e um número para status
 * HTTP. Só 401/403 são veredito sobre a sessão; 5xx, 429 e erro de rede dizem apenas
 * que o servidor não respondeu agora. Na dúvida (formato inesperado), tratamos como
 * transitório — o custo de errar aqui é uma tela a mais, não um logout indevido.
 */
function isDefinitiveAuthFailure(error: unknown): boolean {
  const status = (error as { status?: unknown } | undefined)?.status;
  return status === 401 || status === 403;
}

export function AuthBootstrap({ children }: AuthBootstrapProps) {
  const dispatch = useAppDispatch();
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadMe(token: string): Promise<boolean> {
      const result = await dispatch(
        authApi.endpoints.me.initiate(undefined, { forceRefetch: true }),
      ).unwrap();
      if (cancelled) return true;
      dispatch(sessionStarted({ user: result, accessToken: token }));
      // Sessão de pé: liga a renovação antecipada para o token não vencer em uso.
      startTokenRefreshScheduler();
      return true;
    }

    async function hydrate() {
      let token = getToken();
      let refreshAttempted = false;

      // Sem token persistido. Se nunca houve sessão neste browser, libera direto
      // (evita 401 ruidoso na página de login pra visitante novo). Caso contrário,
      // tenta UMA vez refresh via cookie httpOnly — o cookie pode sobreviver após
      // sessionStorage zerar (fechar/abrir aba).
      if (!token) {
        if (!hasPriorSession()) {
          if (!cancelled) setHydrated(true);
          return;
        }
        refreshAttempted = true;
        const refreshed = await refreshAccessToken();
        if (cancelled) return;
        if (!refreshed.ok) {
          // Só limpa a flag se o servidor RECUSOU. Em falha transitória a flag fica,
          // pra próxima visita tentar de novo com o cookie (que continua válido).
          if (refreshed.reason === 'auth') clearSession();
          setHydrated(true);
          return;
        }
        token = refreshed.token;
      }

      try {
        // Valida via /v1/me (RTK Query → cache populado pra resto do app).
        await loadMe(token);
        if (!cancelled) setHydrated(true);
      } catch (error) {
        if (cancelled) return;

        // Backend fora, offline, timeout: mantém token E flag. A sessão pode estar
        // perfeitamente viva — o app sobe e as próximas requisições se recuperam.
        if (!isDefinitiveAuthFailure(error)) {
          setHydrated(true);
          return;
        }

        // 401/403: o token não presta. Se ainda não tentamos o cookie, tenta agora —
        // o access token pode ter expirado enquanto a aba estava fechada.
        if (!refreshAttempted) {
          const refreshed = await refreshAccessToken();
          if (cancelled) return;
          if (refreshed.ok) {
            try {
              await loadMe(refreshed.token);
              if (!cancelled) setHydrated(true);
              return;
            } catch (retryError) {
              if (cancelled) return;
              if (!isDefinitiveAuthFailure(retryError)) {
                setHydrated(true);
                return;
              }
            }
          } else if (refreshed.reason === 'transient') {
            setHydrated(true);
            return;
          }
        }

        clearSession();
        setHydrated(true);
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!hydrated) return <Splash />;
  return <>{children}</>;
}

export default AuthBootstrap;
